import moment from "moment";
import Paper from "components/general/PaperComponent";
import Text from "components/general/Text";
import UserInfo from "components/cards/UserInfo";
import ModalWrapper from "components/modals/ModalWrapper";
import { useRecalculateMutation } from "apis/services/timekeeper";
import { AllStaffShiftsProps } from "apis/types/shift";
import { User } from "apis/types/auth";
import showSuccessMsg from "utils/showSuccessMsg";
import formatTime from "utils/formatDateTime";
import COLORS from "constants/Colors";
import getBackendError from "utils/getBackendError";
import styles from "./styles.module.scss";

interface RecalculateShiftProps {
  isVisible: boolean;
  setIsVisible: (state: boolean) => void;
  shift?: AllStaffShiftsProps | null;
  employee?: User | null;
}

// Resolution status is what the timekeeper resolved for the shift, so show the
// punch sides alongside it — that is what recalculation is going to move.
const getResolutionStatus = (shift?: AllStaffShiftsProps | null) => {
  if (!shift) return "-";
  const hasCheckIn = !!shift?.checkin?.checkin_time;
  const hasCheckOut = !!shift?.checkin?.checkout_time;
  if (hasCheckIn && hasCheckOut) return shift?.status || "Resolved";
  if (!hasCheckIn && !hasCheckOut) return "Open - no punches resolved";
  return hasCheckIn ? "Open - missing check out" : "Open - missing check in";
};

export default function RecalculateShift({
  isVisible,
  setIsVisible,
  shift,
  employee,
}: RecalculateShiftProps) {
  const [recalculate, { isLoading, error, reset }] = useRecalculateMutation();

  const onConfirm = () => {
    // Guarding on isLoading keeps a double click from firing two requests.
    if (!shift?.id || isLoading) return;
    reset();
    recalculate({ shift_id: shift.id })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Shift has been recalculated successfully!" });
        setIsVisible(false);
      })
      // The error middleware toasts it, the inline message below keeps it up.
      .catch(() => {});
  };

  const errorMessage = getBackendError(error);

  const details = [
    {
      title: "Shift Date",
      value: shift?.date ? moment(shift.date).format("DD-MM-YYYY") : "-",
    },
    {
      title: "Shift Name",
      value: shift?.shift_name || "-",
    },
    {
      title: "Scheduled Start",
      value: formatTime(shift?.start_time ?? null),
    },
    {
      title: "Scheduled End",
      value: formatTime(shift?.end_time ?? null),
    },
    {
      title: "Current Resolution Status",
      value: getResolutionStatus(shift),
    },
    {
      title: "Recorded Punches",
      value: `${shift?.checkin?.checkin_time ? "Check in" : "No check in"} / ${
        shift?.checkin?.checkout_time ? "Check out" : "No check out"
      }`,
    },
  ];

  return (
    <ModalWrapper
      headerTitle="Recalculate shift"
      size="large"
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      onConfirm={onConfirm}
      isLoading={isLoading}
      isBtnDisabled={!shift?.id || isLoading}
    >
      <div className={styles.container}>
        <Paper
          backgroundColor={COLORS.light}
          borderColor={COLORS.lightGreyBackground}
          paddingVertical={24}
          paddingHorizontal={28}
        >
          <UserInfo data={employee ?? shift?.employee} isTableCol />
        </Paper>

        <div className={styles.detailsGrid}>
          {details.map((item) => (
            <div key={item.title} className={styles.detailCard}>
              <Text className={styles.detailTitle}>{item.title}</Text>
              <Text className={styles.detailValue}>{item.value}</Text>
            </div>
          ))}
        </div>

        <Text className={styles.noticeText}>
          Recalculating re-resolves this shift&apos;s punches. Manually assigned
          punches stay locked in place.
        </Text>

        {!!errorMessage && (
          <Text className={styles.errorText}>{errorMessage}</Text>
        )}
      </div>
    </ModalWrapper>
  );
}
